import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, ConfigProvider, Input, Radio, Select, Spin, Tag, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import {
    CONFLICT_ERROR_CODES,
    DRAWER_WIDTH,
    ENTRY_POINT_COLOR,
    PARKING_POINT_COLOR,
    SOURCE_GROUP_LABELS,
    produceErrorText,
    toSourceGroup,
} from '../constants';
import {
    AccessCodeDraft,
    BizTagDraft,
    DrawerPoint,
    EFFECTIVE_STATUS_OPTIONS,
    GeoJsonGeometry,
    PickTarget,
    PickedPoint,
    levelEnumLabel,
    numericLevelToEnum,
} from '../types';
import { areaSqm, centroidOf, countVertices, formatArea, parseGeometry, validateAoiGeometry } from '../utils/geometry';
import { findDuplicateAccessCode, toAccessCodeDrafts, toAccessCodeInputs } from '../utils/accessCode';
import { BizAoiAttributeDetail } from '../../aoi-platform/graphql/queries/getAoiAttributeDetail';
import {
    BizAoiAttributeUpsertInput,
    UPSERT_AOI_ATTRIBUTE,
    UpsertAoiAttributeResponse,
} from '../../aoi-platform/graphql/mutations/upsertAoiAttribute';
import {
    DeliveryTime,
    emptyDeliveryTime,
    parseDeliveryTime,
    serializeDeliveryTime,
    validateDeliveryTime,
} from '../../aoi-platform/utils/deliveryTime';
import { CREATE_BIZ_AOI, CreateBizAoiResponse } from '../graphql/mutations/createBizAoi';
import { UPDATE_BIZ_AOI_GEOMETRY, UpdateBizAoiGeometryResponse } from '../graphql/mutations/updateBizAoiGeometry';
import { UPDATE_BIZ_AOI_BASIC, UpdateBizAoiBasicResponse } from '../graphql/mutations/updateBizAoiBasic';
import { BizAoiProduceResult } from '../graphql/mutations/produceResult';
import AccessCodeEditor from './AccessCodeEditor';
import PointListEditor from './PointListEditor';
import OpenHoursEditor from './OpenHoursEditor';
import RelatedAddressEditor from './RelatedAddressEditor';
import BizTagEditor from './BizTagEditor';
import { toBizTagDrafts, toBizTagInputs } from '../utils/bizTag';
import { AoiBizTagDictItem } from '../graphql/queries/listAoiBizTagDictionary';

/* 抽屉滑入动画 (一次性注入) */
const DRAWER_STYLE_ID = '__aoi_v2_drawer_style__';
if (typeof document !== 'undefined' && !document.getElementById(DRAWER_STYLE_ID)) {
    const style = document.createElement('style');
    style.id = DRAWER_STYLE_ID;
    style.textContent = '@keyframes aoiV2DrawerIn{from{transform:translateX(26px);opacity:0}to{transform:none;opacity:1}}';
    document.head.appendChild(style);
}

export interface AoiDrawerProps {
    mode: 'create' | 'edit';
    detail: BizAoiAttributeDetail | null;
    detailLoading: boolean;
    /** 列表选中时带来的创建时间 (detail 接口无此字段); null = 未知。 */
    createdAtText: string | null;
    /** create 模式: 刚画好的几何。 */
    draftGeometry: GeoJsonGeometry | null;
    /** edit 模式: 未保存的形状改动 (null = 没改)。 */
    editedGeometry: GeoJsonGeometry | null;
    isEditingShape: boolean;
    canEdit: boolean;
    /** 业务标签字典 (页面一次拉取, 抽屉打标 / 动作按钮用)。 */
    tagDictionary: AoiBizTagDictItem[];
    /** 字典加载失败 (且没有任何可用项): 标签区显示失败 + 重试, 不再假装"加载中" */
    tagDictionaryError?: boolean;
    onRetryTagDictionary?: () => void;
    pickTarget: PickTarget;
    onPickTargetChange: (t: PickTarget) => void;
    pickedPoint: PickedPoint | null;
    /** 点位列表变化时上抛 (地图打点显示)。 */
    onPointsChange: (entry: DrawerPoint[], parking: DrawerPoint[]) => void;
    onClose: () => void;
    onDelete: () => void;
    onToggleEditShape: () => void;
    /** createBizAoi 成功回调 (页面选中新 AOI + 刷新视口)。 */
    onCreated: (result: BizAoiProduceResult) => void;
    /** 编辑保存成功回调; statusChanged 时页面要让瓦片失效 (面在已生效/未生效两套瓦片里进出)。 */
    onSaved: (opts: { geometryChanged: boolean; statusChanged: boolean; status: number; tagsChanged: boolean }) => void;
    /** 保存被拒且带冲突面时回调 (页面标红 + fitBounds)。 */
    onConflict: (ids: number[]) => void;
    refetchDetail: () => Promise<unknown>;
}

/** 表单项标签 (antd Form 的字号/颜色口径)。 */
const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 14,
    color: 'rgba(0,0,0,.88)',
    marginBottom: 8,
};

/** 来源 → antd Tag 预设色 (与列表项一致)。 */
const SOURCE_TAG_COLOR: Record<string, string> = {
    MANUAL: 'blue',
    SURVEY: 'gold',
    OVERTURE: 'default',
};

/** 只读键值行 (antd Descriptions 的观感, 但更紧凑)。 */
const readonlyRow = (label: string, value: React.ReactNode) => (
    <div className="flex justify-between py-1 text-xs">
        <span className="text-gray-500">{label}</span>
        <span className="text-right" style={{ color: 'rgba(0,0,0,.88)' }}>{value}</span>
    </div>
);

/** 点位输入 (过滤掉 null 坐标)。 */
function toDrawerPoints(points: { lng: number | null; lat: number | null; remark?: string | null }[] | undefined | null): DrawerPoint[] {
    return (points ?? [])
        .filter((p): p is { lng: number; lat: number; remark?: string | null } => p.lng != null && p.lat != null)
        .map((p) => ({ lng: p.lng, lat: p.lat, remark: p.remark ?? null }));
}

/**
 * 右侧属性抽屉 (420px, 对齐原型风格)。
 *
 * <p>新建: 必选 1级/2级 + 名称 + 点位 + 开门时间 → createBizAoi 一次性提交;
 * <p>已有: 名称/类型/生效状态走 updateBizAoiBasic, 点位/开门时间走 upsertAoiAttribute,
 * 形状走 updateBizAoiGeometry - 一个保存按钮按 dirty 分别调用。
 * 生效状态 未生效→已生效 服务端做互斥校验, 冲突走与形状保存同一套 handleProduceFailure 标红。
 */
const AoiDrawer: React.FC<AoiDrawerProps> = ({
    mode,
    detail,
    detailLoading,
    createdAtText,
    draftGeometry,
    editedGeometry,
    isEditingShape,
    canEdit,
    tagDictionary,
    tagDictionaryError = false,
    onRetryTagDictionary,
    pickTarget,
    onPickTargetChange,
    pickedPoint,
    onPointsChange,
    onClose,
    onDelete,
    onToggleEditShape,
    onCreated,
    onSaved,
    onConflict,
    refetchDetail,
}) => {
    const [name, setName] = useState('');
    const [levelEnum, setLevelEnum] = useState<string | null>(null);
    const [typeCode, setTypeCode] = useState<string | null>(null);
    const [entryPoints, setEntryPoints] = useState<DrawerPoint[]>([]);
    const [parkingPoints, setParkingPoints] = useState<DrawerPoint[]>([]);
    const [accessCodes, setAccessCodes] = useState<AccessCodeDraft[]>([]);
    const [bizTags, setBizTags] = useState<BizTagDraft[]>([]);
    const [deliveryTime, setDeliveryTime] = useState<DeliveryTime>(() => emptyDeliveryTime());
    /** 生效状态 (biz_aoi.status, 1/0); detail 不带时按已生效兜底 (存量 1 级 AOI 默认已生效)。 */
    const [effectiveStatus, setEffectiveStatus] = useState<number>(1);
    const [errorText, setErrorText] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    /** 抽屉根节点: 给 antd 浮层做兜底挂载点 (见 return 处注释)。 */
    const drawerRef = useRef<HTMLDivElement | null>(null);


    const [createMutation] = useMutation<CreateBizAoiResponse>(CREATE_BIZ_AOI);
    const [updateGeometryMutation] = useMutation<UpdateBizAoiGeometryResponse>(UPDATE_BIZ_AOI_GEOMETRY);
    const [updateBasicMutation] = useMutation<UpdateBizAoiBasicResponse>(UPDATE_BIZ_AOI_BASIC);
    const [upsertAttributeMutation] = useMutation<UpsertAoiAttributeResponse>(UPSERT_AOI_ATTRIBUTE);

    /* ====================== 初始化 / 重置 ====================== */

    const detailKey = mode === 'edit' ? detail?.aoiId ?? null : null;
    useEffect(() => {
        setErrorText(null);
        onPickTargetChange(null);
        if (mode === 'create') {
            setName('');
            setLevelEnum(null);
            setTypeCode(null);
            setEntryPoints([]);
            setParkingPoints([]);
            setAccessCodes([]);
            setBizTags([]);
            setDeliveryTime(emptyDeliveryTime());
            setEffectiveStatus(1);
            return;
        }
        if (detail) {
            setName(detail.displayName ?? '');
            setLevelEnum(numericLevelToEnum(detail.level));
            setTypeCode(detail.attribute?.typeCode ?? null);
            setEntryPoints(toDrawerPoints(detail.attribute?.entryPoints));
            setParkingPoints(toDrawerPoints(detail.attribute?.parkingPoints));
            setAccessCodes(toAccessCodeDrafts(detail.attribute?.accessCodes));
            setBizTags(toBizTagDrafts(detail.attribute?.bizTags));
            setDeliveryTime(parseDeliveryTime(detail.attribute?.deliveryTime));
            setEffectiveStatus(detail.status ?? 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, detailKey, detail?.attribute?.updatedAt, detail?.status]);

    /* ====================== 地图选点回流 ====================== */

    useEffect(() => {
        if (!pickedPoint || !pickTarget) return;
        const point: DrawerPoint = { lng: pickedPoint.lng, lat: pickedPoint.lat, remark: null };
        if (pickTarget === 'entry') setEntryPoints((prev) => [...prev, point]);
        else setParkingPoints((prev) => [...prev, point]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pickedPoint?.nonce]);

    // 点位变化上抛给地图打点
    useEffect(() => {
        onPointsChange(entryPoints, parkingPoints);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entryPoints, parkingPoints]);

    /* ====================== 派生数据 ====================== */

    const detailGeometry = useMemo(
        () => parseGeometry(detail?.selfGeometry?.geometryGeoJson),
        [detail?.selfGeometry?.geometryGeoJson],
    );
    const geometry: GeoJsonGeometry | null = mode === 'create' ? draftGeometry : (editedGeometry ?? detailGeometry);
    const vertexCount = useMemo(() => countVertices(geometry), [geometry]);
    const area = useMemo(() => areaSqm(geometry), [geometry]);
    const center = useMemo(() => {
        if (mode === 'edit' && !editedGeometry && detail?.centerLatitude != null && detail?.centerLongitude != null) {
            return { lat: detail.centerLatitude, lng: detail.centerLongitude };
        }
        const c = centroidOf(geometry);
        return c ? { lat: c.lat, lng: c.lng } : null;
    }, [mode, editedGeometry, detail?.centerLatitude, detail?.centerLongitude, geometry]);

    // 几何实时软校验 (红字提示 + 禁保存)
    const geometryValidation = useMemo(() => {
        if (mode === 'create') {
            return validateAoiGeometry(geometry, levelEnum);
        }
        if (editedGeometry) {
            return validateAoiGeometry(editedGeometry, numericLevelToEnum(detail?.level ?? null));
        }
        return { ok: true } as const;
    }, [mode, geometry, levelEnum, editedGeometry, detail?.level]);


    const sourceGroup = detail ? toSourceGroup(detail.dataSource) : null;

    /* ====================== 保存 ====================== */

    const handleProduceFailure = (result: BizAoiProduceResult) => {
        const text = produceErrorText(result.errorCode, result.errorMessage);
        setErrorText(text);
        message.error(text);
        if (result.errorCode && CONFLICT_ERROR_CODES.has(result.errorCode)) {
            const ids = result.conflictAoiIds ?? [];
            onConflict(ids);
            if (result.conflictTruncated) {
                message.warning('冲突面过多, 仅显示部分');
            }
        }
    };

    const handleSaveCreate = async () => {
        if (!draftGeometry) return;
        if (!levelEnum) {
            setErrorText('请选择 AOI 级别 (1级 / 2级)');
            return;
        }
        const geomCheck = validateAoiGeometry(draftGeometry, levelEnum);
        if (!geomCheck.ok) {
            setErrorText(geomCheck.message ?? '几何校验失败');
            return;
        }
        const dtCheck = validateDeliveryTime(deliveryTime);
        if (!dtCheck.ok) {
            setErrorText(dtCheck.message ?? '开门时间校验失败');
            return;
        }
        const codeDup = findDuplicateAccessCode(toAccessCodeInputs(accessCodes));
        if (codeDup) {
            setErrorText(codeDup);
            return;
        }
        setSaving(true);
        setErrorText(null);
        try {
            const { data } = await createMutation({
                variables: {
                    input: {
                        geometryGeoJson: JSON.stringify(draftGeometry),
                        aoiLevel: levelEnum,
                        name: name.trim() || null,
                        typeCode: typeCode ?? null,
                        entryPoints: entryPoints.map((p) => ({ lng: p.lng, lat: p.lat, remark: p.remark ?? null })),
                        parkingPoints: parkingPoints.map((p) => ({ lng: p.lng, lat: p.lat, remark: p.remark ?? null })),
                        accessCodes: toAccessCodeInputs(accessCodes),
                        deliveryTime: serializeDeliveryTime(deliveryTime),
                    },
                },
            });
            const result = data?.createBizAoi;
            if (!result) {
                setErrorText('创建失败: 服务无响应');
                return;
            }
            if (!result.success) {
                handleProduceFailure(result);
                return;
            }
            message.success(`AOI 创建成功 (AOI-${result.aoiId} · ${result.zipcode ?? '-'})`);
            onConflict([]);
            onCreated(result);
        } catch (err) {
            setErrorText(`创建失败: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
            setSaving(false);
        }
    };

    const handleSaveEdit = async () => {
        if (!detail || detail.zipcode == null) return;
        const aoiId = detail.aoiId;
        const zipcode = detail.zipcode;

        const geometryChanged = editedGeometry != null;
        const nameChanged = name.trim() !== (detail.displayName ?? '').trim();
        const typeChanged = (typeCode ?? null) !== (detail.attribute?.typeCode ?? null);
        const statusChanged = effectiveStatus !== (detail.status ?? 1);
        const attrsChanged = JSON.stringify({
            e: entryPoints,
            p: parkingPoints,
            c: toAccessCodeInputs(accessCodes),
            t: toBizTagInputs(bizTags),
            d: serializeDeliveryTime(deliveryTime),
        }) !== JSON.stringify({
            e: toDrawerPoints(detail.attribute?.entryPoints),
            p: toDrawerPoints(detail.attribute?.parkingPoints),
            c: toAccessCodeInputs(toAccessCodeDrafts(detail.attribute?.accessCodes)),
            t: toBizTagInputs(toBizTagDrafts(detail.attribute?.bizTags)),
            d: serializeDeliveryTime(parseDeliveryTime(detail.attribute?.deliveryTime)),
        });
        // 标签单独比: 没动过就传 null (契约 null = 不改), 否则一次与标签无关的保存会把已有标签全量回传,
        // 服务端对回传的每个标签校验"已停用 / 层级", 字典一变 (停用某标签 / 面改层级) 这个面什么属性都存不了
        const tagsChanged = JSON.stringify(toBizTagInputs(bizTags))
            !== JSON.stringify(toBizTagInputs(toBizTagDrafts(detail.attribute?.bizTags)));

        if (!geometryChanged && !nameChanged && !typeChanged && !statusChanged && !attrsChanged) {
            message.info('没有需要保存的改动');
            return;
        }

        if (geometryChanged) {
            const geomCheck = validateAoiGeometry(editedGeometry, numericLevelToEnum(detail.level));
            if (!geomCheck.ok) {
                setErrorText(geomCheck.message ?? '几何校验失败');
                return;
            }
        }
        if (attrsChanged) {
            const dtCheck = validateDeliveryTime(deliveryTime);
            if (!dtCheck.ok) {
                setErrorText(dtCheck.message ?? '开门时间校验失败');
                return;
            }
            const codeDup = findDuplicateAccessCode(toAccessCodeInputs(accessCodes));
            if (codeDup) {
                setErrorText(codeDup);
                return;
            }
        }

        setSaving(true);
        setErrorText(null);
        try {
            // 1. 几何 (失败中断, 冲突标红)
            if (geometryChanged && editedGeometry) {
                const { data } = await updateGeometryMutation({
                    variables: { input: { aoiId, zipcode, geometryGeoJson: JSON.stringify(editedGeometry) } },
                });
                const result = data?.updateBizAoiGeometry;
                if (!result?.success) {
                    if (result) handleProduceFailure(result);
                    else setErrorText('几何保存失败: 服务无响应');
                    return;
                }
            }
            // 2. 基础信息 (名称 / 类型 / 生效状态); status 只在变了才传, 服务端 null=不改
            if (nameChanged || typeChanged || statusChanged) {
                const { data } = await updateBasicMutation({
                    variables: {
                        input: {
                            aoiId,
                            zipcode,
                            name: name.trim() || null,
                            typeCode: typeCode ?? null,
                            status: statusChanged ? effectiveStatus : null,
                        },
                    },
                });
                const result = data?.updateBizAoiBasic;
                if (!result?.success) {
                    // 未生效→已生效 的互斥冲突也走这里: 冲突面标红, 与形状保存同一套体验
                    if (result) handleProduceFailure(result);
                    else setErrorText('基础信息保存失败: 服务无响应');
                    return;
                }
            }
            // 3. 衍生属性 (点位 / 开门时间; typeCode 同步带上保持两处一致)
            if (attrsChanged) {
                const input: BizAoiAttributeUpsertInput = {
                    aoiId,
                    zipcode,
                    entryPoints: entryPoints.map((p) => ({ lng: p.lng, lat: p.lat, remark: p.remark ?? null })),
                    parkingPoints: parkingPoints.map((p) => ({ lng: p.lng, lat: p.lat, remark: p.remark ?? null })),
                    accessCodes: toAccessCodeInputs(accessCodes),
                    deliveryTime: serializeDeliveryTime(deliveryTime),
                    typeCode: typeCode ?? null,
                    // 动过才传 (空数组 = 清空); 没动传 null = 不改, 服务端不重新校验也不碰缓存
                    bizTags: tagsChanged ? toBizTagInputs(bizTags) : null,
                };
                await upsertAttributeMutation({ variables: { input } });
            }
            message.success('保存成功');
            onConflict([]);
            onSaved({ geometryChanged, statusChanged, status: effectiveStatus, tagsChanged });
            await refetchDetail();
        } catch (err) {
            setErrorText(`保存失败: ${err instanceof Error ? err.message : String(err)}`);
        } finally {
            setSaving(false);
        }
    };

    const handleSave = () => {
        if (mode === 'create') void handleSaveCreate();
        else void handleSaveEdit();
    };

    /* ====================== 渲染 ====================== */

    const headerId = mode === 'create'
        ? '新建'
        : detail ? `AOI-${detail.aoiId}` : '...';


    return (
        // 下拉 / 时间选择器的浮层必须挂在抽屉内部, 不能用 antd 默认的 document.body:
        // 抽屉自己带 transform 滑入动画, 而 transform 会创建新的包含块 —— 挂在 body 上的浮层
        // 按 body 坐标系定位, 结果飘到页面左上角 (被筛选区盖住那种)。
        // 挂到抽屉上还有个附带好处: 抽屉内容滚动时浮层跟着走, 不会留在原地。
        <ConfigProvider getPopupContainer={(trigger) => (trigger?.parentElement ?? drawerRef.current ?? document.body)}>
        <div
            ref={drawerRef}
            className="absolute bottom-0 right-0 top-0 flex flex-col bg-white"
            style={{
                width: DRAWER_WIDTH,
                zIndex: 1300,
                borderLeft: '1px solid #f0f0f0',
                boxShadow: '-6px 0 16px rgba(0,0,0,.08)',
                animation: 'aoiV2DrawerIn .22s ease-out',
            }}
        >
            {/* 头部 (antd Drawer 头部语言: 标题 + 关闭按钮 + 标签) */}
            <div
                className="flex flex-none items-start justify-between gap-3 px-4 py-4"
                style={{ borderBottom: '1px solid #f0f0f0' }}
            >
                <div style={{ minWidth: 0 }}>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{headerId}</span>
                        {mode === 'edit' && detail && (
                            <>
                                <Tag className="!mr-0" color="blue">
                                    {levelEnumLabel(numericLevelToEnum(detail.level))}
                                </Tag>
                                {sourceGroup && (
                                    <Tag className="!mr-0" color={SOURCE_TAG_COLOR[sourceGroup]}>
                                        {SOURCE_GROUP_LABELS[sourceGroup]}
                                    </Tag>
                                )}
                            </>
                        )}
                    </div>
                    <div className="mt-1.5 text-base font-medium">
                        {mode === 'create' ? '新建 AOI' : 'AOI 属性'}
                    </div>
                </div>
                <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />
            </div>

            {/* 内容 */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
                {mode === 'edit' && detailLoading && !detail ? (
                    <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {/* 名称 */}
                        <div>
                            <label style={labelStyle}>名称</label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                readOnly={!canEdit}
                                placeholder="AOI 名称"
                            />
                        </div>

                        {/* 级别 (类型本期不做: 后端与请求体仍保留 typeCode 预留口子, 需要时把下拉加回本行) */}
                        <div>
                            <label style={labelStyle}>
                                级别{mode === 'create' && <span style={{ color: '#ff4d4f' }}> *</span>}
                            </label>
                            {mode === 'create' ? (
                                <Radio.Group
                                    value={levelEnum}
                                    onChange={(e) => setLevelEnum(e.target.value)}
                                    optionType="button"
                                    buttonStyle="solid"
                                    style={{ width: '100%' }}
                                >
                                    <Radio.Button value="LEVEL_1" style={{ width: '50%', textAlign: 'center' }}>1级</Radio.Button>
                                    <Radio.Button value="LEVEL_2" style={{ width: '50%', textAlign: 'center' }}>2级</Radio.Button>
                                </Radio.Group>
                            ) : (
                                <Input value={levelEnumLabel(numericLevelToEnum(detail?.level))} disabled />
                            )}
                        </div>

                        {/* Zipcode (只读) */}
                        <div>
                            <label style={labelStyle}>Zipcode</label>
                            <Input
                                value={mode === 'create' ? '保存后按中心点自动解析' : detail?.zipcode ?? '-'}
                                disabled
                            />
                        </div>

                        {/* 生效状态 = 人工审核结果 (V2.1, 2026-09-09): 位置按 PRD 放 zipcode 下。
                            手绘新建的面保存后默认已生效, 新建态不给改; 存量面默认已生效, 可改 */}
                        <div>
                            <label style={labelStyle}>生效状态</label>
                            {mode === 'create' ? (
                                <Input value="保存后默认已生效" disabled />
                            ) : (
                                <Select
                                    value={effectiveStatus}
                                    options={EFFECTIVE_STATUS_OPTIONS}
                                    disabled={!canEdit}
                                    onChange={setEffectiveStatus}
                                    style={{ width: '100%' }}
                                />
                            )}
                            {mode === 'edit' && detail && effectiveStatus !== (detail.status ?? 1) && (
                                <div className="mt-1 text-xs" style={{ color: '#d48806' }}>
                                    {effectiveStatus === 1
                                        // 拍板 B / C: 生效前互斥校验; 生效后不自动挂地址, 关联地址要另行补齐
                                        ? '保存时会校验与既有面是否重叠; 生效后关联地址不会自动补齐'
                                        // 拍板 D: 下线不清 mapping, 但线上 lookup 回查主表 status, 该面即刻退出匹配
                                        : '保存后该面退出线上地址匹配; 已有关联地址保留不清理'}
                                </div>
                            )}
                        </div>

                        {/* 几何 / 位置 (只读) */}
                        <div className="rounded-lg p-3" style={{ background: '#fafafa', border: '1px solid #f0f0f0' }}>
                            <div className="mb-2 text-sm" style={{ color: 'rgba(0,0,0,.88)' }}>几何 / 位置</div>
                            {readonlyRow('中心点', center ? `${center.lat.toFixed(5)}, ${center.lng.toFixed(5)}` : '-')}
                            {readonlyRow('顶点数', vertexCount || '-')}
                            {readonlyRow('面积 (估)', geometry ? formatArea(area) : '-')}
                            {editedGeometry && (
                                <div className="mt-1 text-xs" style={{ color: '#d48806' }}>形状已修改, 保存后生效</div>
                            )}
                            {!geometryValidation.ok && (
                                <div className="mt-1 text-xs" style={{ color: '#ff4d4f' }}>{geometryValidation.message}</div>
                            )}
                        </div>

                        {/* 进门点 / 停车点 */}
                        <PointListEditor
                            label="进门点"
                            badgePrefix="门"
                            badgeColor={ENTRY_POINT_COLOR.hex}
                            points={entryPoints}
                            picking={pickTarget === 'entry'}
                            readonly={!canEdit}
                            onTogglePick={() => onPickTargetChange(pickTarget === 'entry' ? null : 'entry')}
                            onRemove={(i) => setEntryPoints((prev) => prev.filter((_, j) => j !== i))}
                        />
                        <PointListEditor
                            label="停车点"
                            badgePrefix="P"
                            badgeColor={PARKING_POINT_COLOR.hex}
                            points={parkingPoints}
                            picking={pickTarget === 'parking'}
                            readonly={!canEdit}
                            onTogglePick={() => onPickTargetChange(pickTarget === 'parking' ? null : 'parking')}
                            onRemove={(i) => setParkingPoints((prev) => prev.filter((_, j) => j !== i))}
                        />

                        {/* 门禁码 (AOI 自己的门禁, 随衍生属性一起提交) */}
                        <AccessCodeEditor codes={accessCodes} readonly={!canEdit} onChange={setAccessCodes} />

                        {/* 关联地址 */}
                        {mode === 'edit' && detail && detail.zipcode != null && (
                            <RelatedAddressEditor
                                aoiId={detail.aoiId}
                                zipcode={detail.zipcode}
                                addresses={detail.relatedAddresses}
                                readonly={!canEdit}
                                onChanged={() => { void refetchDetail(); }}
                            />
                        )}

                        {/* 开门时间 */}
                        <OpenHoursEditor value={deliveryTime} onChange={setDeliveryTime} readonly={!canEdit} />

                        {/* 业务标签 (随衍生属性 upsert 一起提交; 新建态要等 AOI 保存后才能打) */}
                        <BizTagEditor
                            mode={mode}
                            aoiId={mode === 'edit' ? detail?.aoiId ?? null : null}
                            zipcode={mode === 'edit' ? detail?.zipcode ?? null : null}
                            aoiDbLevel={mode === 'edit' ? detail?.level ?? null : null}
                            dictionary={tagDictionary}
                            dictionaryError={tagDictionaryError}
                            onRetryDictionary={onRetryTagDictionary}
                            tags={bizTags}
                            savedTagCodes={(detail?.attribute?.bizTags ?? []).map((t) => t.tag)}
                            readonly={!canEdit}
                            onChange={setBizTags}
                        />

                        {/* 创建时间放内容区最后 (对齐产品 demo 的"创建于 YYYY-MM-DD"灰字) */}
                        {mode === 'edit' && createdAtText && (
                            <div className="text-xs" style={{ color: '#a5adb9' }}>
                                创建于 {createdAtText.replace('T', ' ').slice(0, 10)}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* 错误提示 + 底部按钮 */}
            <div className="flex-none" style={{ borderTop: '1px solid #f0f0f0' }}>
                {errorText && (
                    <div className="px-4 pt-2 text-xs" style={{ color: '#ff4d4f' }}>{errorText}</div>
                )}
                <div className="flex gap-2 px-4 py-3">
                    {canEdit && mode === 'edit' && (
                        <Button danger onClick={onDelete}>删除</Button>
                    )}
                    {canEdit && mode === 'edit' && (
                        <Button
                            block
                            type={isEditingShape ? 'primary' : 'default'}
                            ghost={isEditingShape}
                            onClick={onToggleEditShape}
                        >
                            {isEditingShape ? '完成编辑' : '编辑形状'}
                        </Button>
                    )}
                    {canEdit && (
                        <Button
                            block
                            type="primary"
                            loading={saving}
                            disabled={!geometryValidation.ok}
                            onClick={handleSave}
                        >
                            保存
                        </Button>
                    )}
                    {!canEdit && <Button block onClick={onClose}>关闭</Button>}
                </div>
            </div>
        </div>
        </ConfigProvider>
    );
};

export default AoiDrawer;
